// UI-17 (Block I): the Team calendar's data, kept out of the page so it can be tested on its own.
//
// Two kinds of date reach this page and they must not be confused:
//   - a DAY ("2026-10-12": a Google all-day event, approved leave). It has no time and no zone, so
//     it is a local calendar date. `new Date('2026-10-12')` reads it as midnight UTC, which west of
//     UTC is the evening of the 11th: the event drew on the wrong day. parseEventDate reads it as a
//     local date, and leave is never turned into a Date at all (its days are compared as strings).
//   - a TIME. A task's own fields are a bare wall-clock ("2026-10-12T09:00:00"); the caller's own
//     tasks keep the page's existing `new Date(`${date}T${time}`)` reading, unchanged, and a
//     colleague's task from GET /api/calendar/team is read the same way.
//
// Weeks start on Monday (UK convention; GET /api/calendar/preferences defaults weekStartsOn to 1).

const pad = (n) => String(n).padStart(2, '0');
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// 'YYYY-MM-DD' for a Date, in local time.
export function localDay(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// A 'YYYY-MM-DD' day as a local Date at midnight, or null.
export function dayToDate(day) {
  const m = DAY_RE.exec(String(day || '').trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

// Any date a calendar feed gives us: a Date, a date-only day (read as a LOCAL date), or a
// timestamp. null when it will not parse.
export function parseEventDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  const day = dayToDate(value);
  if (day) return day;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function addDays(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

export function addDayString(day, n) {
  return localDay(addDays(dayToDate(day), n));
}

// Monday of the week containing `date`.
export function startOfWeek(date) {
  const offset = (date.getDay() + 6) % 7;            // Monday 0 … Sunday 6
  return addDays(date, -offset);
}

export const AGENDA_DAYS = 14;

// The days a view shows (inclusive), as Dates. Month shows whole Monday-first weeks.
export function visibleRange(view, cursor) {
  if (view === 'month') {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    return { from: startOfWeek(first), to: addDays(startOfWeek(last), 6) };
  }
  const from = startOfWeek(cursor);
  return { from, to: addDays(from, view === 'agenda' ? AGENDA_DAYS - 1 : 6) };
}

export function daysBetween(from, to) {
  const out = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

// ── Event types: the legend and the chip colours (token names) ───────────────────────────────
export const EVENT_TYPES = {
  meeting: { label: 'Meeting', color: 'pastel-blue' },
  task: { label: 'Task', color: 'pastel-yellow' },
  calendar: { label: 'Calendar', color: 'pastel-periwinkle' },
  away: { label: 'Away', color: 'pastel-pink' },
};

export const SLOTS = [
  { key: 'allday', label: 'All day', hours: 'Away and all-day' },
  { key: 'morning', label: 'Morning', hours: 'Until 12:00' },
  { key: 'afternoon', label: 'Afternoon', hours: '12:00 to 18:00' },
  { key: 'evening', label: 'Evening', hours: 'From 18:00' },
];

export function slotOf(event) {
  if (event.allDay) return 'allday';
  const h = event.start.getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

export function isOnDay(event, day) {
  return event.startDay <= day && day <= event.endDay;
}

export function overlaps(event, fromDay, toDay) {
  return event.startDay <= toDay && event.endDay >= fromDay;
}

export function sortEvents(a, b) {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  if (a.allDay) return a.startDay.localeCompare(b.startDay) || a.title.localeCompare(b.title);
  return a.start - b.start || a.title.localeCompare(b.title);
}

const timed = (base, start) => ({ ...base, allDay: false, start, startDay: localDay(start), endDay: localDay(start) });

// An all-day event from feed dates. The feed's end is EXCLUSIVE (the day after), so the last day
// shown is the day before it; a missing or same-day end is a one-day event.
function allDayFrom(base, startValue, endValue) {
  const start = parseEventDate(startValue);
  if (!start) return null;
  const startDay = localDay(start);
  const end = parseEventDate(endValue);
  let endDay = startDay;
  if (end) {
    const lastDay = localDay(addDays(end, -1));
    if (lastDay > startDay) endDay = lastDay;
  }
  return { ...base, allDay: true, start, startDay, endDay };
}

function isMeeting(task) {
  return String(task.taskType || '').toLowerCase() === 'meeting' || !!task.meetingType
    || (Array.isArray(task.invitees) && task.invitees.length > 0);
}

// Build every event the page can show, for everyone, from every source.
//   me: { id, name }; members: [{ id, name }] from the team route (the caller excluded)
//   tasks: the caller's own tasks (App's userSchedule)
//   team: { events, leave } from GET /api/calendar/team
export function buildEvents({ me, members = [], tasks = [], google = [], ical = [], outlook = [], team = {} }) {
  const out = [];
  const meId = me?.id ?? null;
  const nameOf = (id) => members.find((m) => m.id === id)?.name || null;

  // The caller's own tasks, as "you". A task owned by someone else (one assigned to the caller)
  // is theirs: it comes from the team route, where it is shown without Edit or Delete.
  for (const task of tasks) {
    if (!task) continue;
    if (task.userId && meId && task.userId !== meId) continue;
    const taskDateStr = task.date || (task.datetime ? String(task.datetime).slice(0, 10) : null);
    if (!taskDateStr) continue;
    const taskTimeStr = task.time || (task.datetime ? String(task.datetime).slice(11, 16) : '09:00');
    const start = new Date(`${taskDateStr}T${taskTimeStr}`);
    if (Number.isNaN(start.getTime())) continue;
    out.push(timed({
      id: `task-${task.id}`, kind: isMeeting(task) ? 'meeting' : 'task', title: task.title || 'Untitled task',
      ownerId: meId, mine: true, source: 'Aidey', originalTask: task,
      priority: task.priority, details: task.details, location: task.location,
    }, start));
  }

  // Colleagues' items the team route allows (decision 33). Never the caller's own: those are above.
  for (const t of team.events || []) {
    if (!t || !t.datetime || (meId && t.userId === meId)) continue;
    const start = new Date(String(t.datetime).slice(0, 19));
    if (Number.isNaN(start.getTime())) continue;
    out.push(timed({
      id: `team-${t.id}`, kind: 'task', title: t.title || 'Untitled task', ownerId: t.userId,
      mine: false, source: 'Aidey', priority: t.priority, completed: !!t.completed,
    }, start));
  }

  // Approved leave: who and which days, never the type. Days stay strings.
  (team.leave || []).forEach((l, i) => {
    if (!l || !DAY_RE.test(String(l.startDate || ''))) return;
    const endDay = DAY_RE.test(String(l.endDate || '')) && l.endDate >= l.startDate ? l.endDate : l.startDate;
    const mine = !!meId && l.userId === meId;
    const who = mine ? 'You' : (nameOf(l.userId) || 'A former team member');
    out.push({
      id: `leave-${l.userId || 'x'}-${l.startDate}-${i}`, kind: 'away', title: mine ? 'You are away' : `${who} away`,
      ownerId: l.userId || null, mine, source: 'Leave', allDay: true, start: null, startDay: l.startDate, endDay,
    });
  });

  for (const e of google) {
    if (!e) continue;
    const base = { id: `google-${e.id}`, kind: 'calendar', title: e.summary || 'Google event', ownerId: meId, mine: true, source: 'Google Calendar', location: e.location };
    if (e.start?.date) { const ev = allDayFrom(base, e.start.date, e.end?.date); if (ev) out.push(ev); continue; }
    const start = parseEventDate(e.start?.dateTime);
    if (start) out.push({ ...timed(base, start), end: parseEventDate(e.end?.dateTime) });
  }

  for (const e of ical) {
    if (!e) continue;
    const base = { id: `ical-${e.id}`, kind: 'calendar', title: e.title || 'Calendar event', ownerId: meId, mine: true, source: 'Subscribed calendar', location: e.location };
    if (e.allDay || dayToDate(e.start)) { const ev = allDayFrom(base, e.start, e.end); if (ev) out.push(ev); continue; }
    const start = parseEventDate(e.start);
    if (start) out.push({ ...timed(base, start), end: parseEventDate(e.end) });
  }

  for (const e of outlook) {
    if (!e) continue;
    const base = { id: `outlook-${e.id}`, kind: 'calendar', title: e.subject || 'Outlook event', ownerId: meId, mine: true, source: 'Outlook', location: e.location?.displayName };
    if (e.isAllDay) { const ev = allDayFrom(base, String(e.start?.dateTime || '').slice(0, 10), String(e.end?.dateTime || '').slice(0, 10)); if (ev) out.push(ev); continue; }
    const start = e.start?.dateTime ? new Date(e.start.dateTime) : null;
    if (start && !Number.isNaN(start.getTime())) out.push({ ...timed(base, start), end: e.end?.dateTime ? new Date(e.end.dateTime) : null });
  }

  return out.sort(sortEvents);
}
